import {
  Box,
  Button,
  Flex,
  Label,
  Link,
  Text,
  Tooltip,
  useAppTheme,
} from '@workwhile/ui'
import { InvoiceStatus } from 'components/InvoiceStatus'
import * as ROUTES from 'constants/routes'
import { formatCurrency } from 'lib/currency'
import { AdminChargeGroup } from 'api/admin/charges/getCharges'
import { ChevronDown, ChevronRight, Receipt, Settings2 } from 'lucide-react'
import React from 'react'
import {
  formatChargesDate,
  formatChargesDateTime,
  formatChargesRelative,
} from '../formatDateTime'
import { stagingLabel, stagingTooltip, stagingVariant } from '../staging'
import { describeGroupKeyPart, GROUP_KEY_TOOLTIP } from '../groupKey'
import { Detail, DetailRow } from './Detail'
import { InvoiceDownloadButtons } from './InvoiceDownloadButtons'

function toNumber(value: string | number): number {
  return typeof value === 'number' ? value : Number(value)
}

function sumCharges(group: AdminChargeGroup): number {
  return group.charges.reduce(
    (total, charge) => total + toNumber(charge.totalAmount),
    0
  )
}

const stripeInvoiceUrl = (stripeId: string) =>
  `https://dashboard.stripe.com/invoices/${stripeId}`

type ChargeGroupHeaderProps = {
  group: AdminChargeGroup
  isExpanded: boolean
  onToggle: () => void
}

export const ChargeGroupHeader = ({
  group,
  isExpanded,
  onToggle,
}: ChargeGroupHeaderProps) => {
  const { colors } = useAppTheme()

  const headerChrome = {
    mb: 2,
    pb: 2,
    pt: 2,
    backgroundColor: 'white' as const,
    style: {
      borderBottom: `1px solid ${colors.neutrals[100]}`,
      cursor: 'pointer',
    },
  }

  const chargeCount = (
    <Text fontSize={1} color="lightText">
      {group.charges.length} charge{group.charges.length === 1 ? '' : 's'}
    </Text>
  )

  const chevron = (
    <Button
      variant="text"
      kind="medium"
      style={{ padding: 2 }}
      tabIndex={-1}
      onClick={(event) => {
        event.stopPropagation()
        onToggle()
      }}
    >
      {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
    </Button>
  )

  const toggleProps = {
    role: 'button',
    tabIndex: 0,
    'aria-expanded': isExpanded,
    onClick: onToggle,
    onKeyDown: (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        onToggle()
      }
    },
  }

  if (group.kind === 'uninvoiced') {
    const uninvoiced = group.uninvoiced
    const total = sumCharges(group)

    return (
      <Box {...toggleProps} {...headerChrome}>
        <Flex
          justifyContent="space-between"
          alignItems="center"
          gap={3}
          flexWrap="wrap"
          mb={1}
        >
          <Flex alignItems="center" gap={2} flexWrap="wrap">
            {chevron}
            <Text fontSize={3} fontWeight={700}>
              Uninvoiced
            </Text>
            <InvoiceStatus status="uninvoiced" minWidth="auto" />
            {uninvoiced != null ? (
              <Tooltip content={stagingTooltip(uninvoiced.stagingStatus)}>
                <Label
                  variant={stagingVariant(uninvoiced.stagingStatus)}
                  size="small"
                >
                  {stagingLabel(uninvoiced.stagingStatus)}
                </Label>
              </Tooltip>
            ) : null}
            {chargeCount}
          </Flex>

          <Tooltip
            content={`Sum of the ${group.charges.length} charge${
              group.charges.length === 1 ? '' : 's'
            } in this group`}
          >
            <Text
              fontSize={3}
              fontWeight={700}
              color={total < 0 ? 'error' : 'text'}
              style={{ whiteSpace: 'nowrap' }}
            >
              {formatCurrency(total)}
            </Text>
          </Tooltip>
        </Flex>

        {uninvoiced != null ? (
          <Box
            px={3}
            py={2}
            backgroundColor={colors.neutrals[50]}
            style={{
              borderLeft: `3px solid ${colors.neutrals[200]}`,
              borderRadius: 4,
            }}
            onClick={(event) => event.stopPropagation()}
          >
            <Flex
              alignItems="center"
              gap={2}
              mb={2}
              flexWrap="wrap"
              justifyContent="space-between"
            >
              <Flex alignItems="center" gap={2}>
                <Settings2 size={14} color={colors.lightText} />
                <Text fontSize={1} fontWeight={600}>
                  Invoice config — these charges bill together
                </Text>
              </Flex>
              <Link to={ROUTES.INVOICE_CONFIG} target="_blank">
                View invoice configs
              </Link>
            </Flex>

            {uninvoiced.invoiceGroupKey.length > 0 ? (
              <Flex alignItems="center" gap={2} flexWrap="wrap" mb={2}>
                <Tooltip content={GROUP_KEY_TOOLTIP}>
                  <Text
                    fontSize={0}
                    color="lightText"
                    style={{
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                      cursor: 'help',
                    }}
                  >
                    Group key
                  </Text>
                </Tooltip>
                {uninvoiced.invoiceGroupKey.map((part) => (
                  <Tooltip
                    key={part}
                    content={describeGroupKeyPart(
                      part,
                      uninvoiced.invoiceConfig
                    )}
                  >
                    <Label
                      variant="none"
                      size="small"
                      inline
                      style={{ cursor: 'help' }}
                    >
                      {part}
                    </Label>
                  </Tooltip>
                ))}
              </Flex>
            ) : null}

            <DetailRow>
              <Detail label="Cycle">
                {uninvoiced.invoiceConfig.cycleType}
              </Detail>
              <Detail label="Breakdown">
                {uninvoiced.invoiceConfig.breakdownLevels.length > 0
                  ? uninvoiced.invoiceConfig.breakdownLevels.join(', ')
                  : '—'}
              </Detail>
              {uninvoiced.invoiceConfig.customWeekStartDay != null ? (
                <Detail label="Week start">
                  {uninvoiced.invoiceConfig.customWeekStartDay}
                  {uninvoiced.invoiceConfig.customWeekTimezone != null
                    ? ` (${uninvoiced.invoiceConfig.customWeekTimezone})`
                    : null}
                </Detail>
              ) : null}
              <Detail label="Scope">
                {uninvoiced.invoiceConfig.companyGroupId != null ? (
                  <>
                    Company group{' '}
                    <Link
                      to={`${ROUTES.COMPANY_GROUPS}/${uninvoiced.invoiceConfig.companyGroupId}`}
                      target="_blank"
                    >
                      #{uninvoiced.invoiceConfig.companyGroupId}
                    </Link>
                  </>
                ) : uninvoiced.invoiceConfig.companyId != null ? (
                  <>
                    Company{' '}
                    <Link
                      to={`${ROUTES.COMPANIES}/${uninvoiced.invoiceConfig.companyId}`}
                      target="_blank"
                    >
                      #{uninvoiced.invoiceConfig.companyId}
                    </Link>
                  </>
                ) : (
                  '—'
                )}
              </Detail>
              <Detail
                label="Invoice ready at"
                title={
                  uninvoiced.runAt != null
                    ? formatChargesDateTime(uninvoiced.runAt)
                    : undefined
                }
              >
                {uninvoiced.runAt != null ? (
                  <>
                    {formatChargesDateTime(uninvoiced.runAt)}{' '}
                    <Text as="span" color="lightText">
                      ({formatChargesRelative(uninvoiced.runAt)})
                    </Text>
                  </>
                ) : (
                  '—'
                )}
              </Detail>
            </DetailRow>
          </Box>
        ) : null}
      </Box>
    )
  }

  const invoice = group.invoice
  if (invoice == null) {
    return null
  }

  const invoiceTotal = toNumber(invoice.totalAmount)

  return (
    <Box {...toggleProps} {...headerChrome}>
      <Flex
        justifyContent="space-between"
        alignItems="center"
        gap={3}
        flexWrap="wrap"
        mb={1}
      >
        <Flex alignItems="center" gap={2} flexWrap="wrap">
          {chevron}
          <Receipt size={16} color={colors.lightText} />
          <Text fontSize={3} fontWeight={700}>
            Invoice #{invoice.id}
          </Text>
          <InvoiceStatus status={invoice.status} minWidth="auto" />
          <Tooltip
            content={
              invoice.automated
                ? 'Created by the automated invoice run'
                : 'Created manually by an admin'
            }
          >
            <Label
              variant={invoice.automated ? 'information' : 'none'}
              size="small"
            >
              {invoice.automated ? 'Automated' : 'Manual'}
            </Label>
          </Tooltip>
          {chargeCount}
        </Flex>

        <Text
          fontSize={3}
          fontWeight={700}
          color={invoiceTotal < 0 ? 'error' : 'text'}
          style={{ whiteSpace: 'nowrap' }}
        >
          {formatCurrency(invoiceTotal)}
        </Text>
      </Flex>

      <Box
        px={3}
        py={2}
        backgroundColor={colors.neutrals[50]}
        style={{
          borderLeft: `3px solid ${colors.neutrals[200]}`,
          borderRadius: 4,
        }}
        onClick={(event) => event.stopPropagation()}
      >
        <Flex
          alignItems="flex-start"
          justifyContent="space-between"
          gap={3}
          flexWrap="wrap"
        >
          <Box style={{ flex: 1, minWidth: 240 }}>
            <DetailRow>
              <Detail
                label="Created"
                title={formatChargesDateTime(invoice.createdAt)}
              >
                {formatChargesDate(invoice.createdAt)}{' '}
                <Text as="span" color="lightText">
                  ({formatChargesRelative(invoice.createdAt)})
                </Text>
              </Detail>
              <Detail label="Stripe">
                {invoice.stripeId != null ? (
                  <Link
                    to={stripeInvoiceUrl(invoice.stripeId)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {invoice.stripeId}
                  </Link>
                ) : (
                  '—'
                )}
              </Detail>
              <Detail label="Drill in">
                <Link to={`/charges?invoice=${invoice.id}`}>
                  Only this invoice
                </Link>
              </Detail>
            </DetailRow>
            {invoice.memo != null && invoice.memo !== '' ? (
              <Box mt={2}>
                <Detail label="Memo">
                  <Text fontSize={1} whiteSpace="wrap">
                    {invoice.memo}
                  </Text>
                </Detail>
              </Box>
            ) : null}
          </Box>
          <InvoiceDownloadButtons invoiceId={invoice.id} />
        </Flex>
      </Box>
    </Box>
  )
}
